/*
 * Copyright (c) 2025 Certusoft, Inc. All Rights Reserved.
 *
 * This SOURCE CODE FILE, which has been provided by Certusoft as part
 * of a Certusoft product for use ONLY by licensed users of the product,
 * includes CONFIDENTIAL and PROPRIETARY information of Certusoft.
 *
 * USE OF THIS SOFTWARE IS GOVERNED BY THE TERMS AND CONDITIONS
 * OF THE LICENSE STATEMENT AND LIMITED WARRANTY FURNISHED WITH
 * THE PRODUCT.
 */

import { EntityType, HolePatternType } from '../../types/enums'
import _ from 'lodash'
import { MatrixFunctions } from 'src/app/models/matrix-functions'
import * as math from 'mathjs'

/**
 * Conversion functions
 */

export class ThreedUtils {
    // Templates
    static infoTemplate = {
        name: '',
        preTransform: {
            up: '',
            front: '',
        },
        datumList: [],
    }
    static datumTemplate = {
        name: '',
        featureDescriptionOverride: false,
        featureDescription: '',
        status: 'OK',
        loading: 0,
        descriptionDirtyVal: false,
        transformDirtyVal: false,
        saveDirtyVal: false,
        values: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
        transformComponents: {
            rotations: [
                {
                    axis: '',
                    angle: 0.0,
                },
            ],
            translateX: 0,
            translateY: 0,
            translateZ: 0,
        },
    }

    static createPartInfo() {
        let vm = this
        return JSON.parse(JSON.stringify(vm.infoTemplate))
    }

    static createDatum(updateFn?: Function) {
        let vm = this
        let datum = JSON.parse(JSON.stringify(vm.datumTemplate))
        datum.descriptionDirty = (val?: boolean) => {
            if (undefined !== val) {
                datum.descriptionDirtyVal = val
                if (val) {
                    datum.saveDirty(val)
                }
            }
            return datum.descriptionDirtyVal
        }
        datum.transformDirty = (val?: boolean) => {
            if (undefined !== val) {
                datum.transformDirtyVal = val
                if (val) {
                    datum.saveDirty(val)
                }
            }
            return datum.transformDirtyVal
        }
        datum.saveDirty = (val?: boolean) => {
            if (undefined !== val) {
                datum.saveDirtyVal = val
            }
            return datum.saveDirtyVal
        }
        datum.hidden = false
        datum.toggleHidden = () => {
            datum.hidden = !datum.hidden
            updateFn?.()
        }
        datum.getEntityType = () => {
            // Get the most accurate type available
            return datum.getDetectedEntityType() || datum.getDatumEntityType()
        }
        datum.getDatumEntityType = () => {
            let type = datum.datum?.datumType?.name
            if (type) {
                if (type === 'CSYS') {
                    return EntityType.TRANSFORM
                }
                if (type === 'HOLE') {
                    return EntityType.ARC_PAIR
                }
                return type
            }
            return null
        }
        datum.getDetectedEntityType = () => {
            return datum.state?.logicalStructure?.entityType
        }
        datum.entityTypeMatch = true
        return datum
    }

    /**
     * Helper functions
     */

    // Visually distinct colors for feature highlighting
    static colorSet = [
        // https://sashamaps.net/docs/resources/20-colors/
        { hex: '#e6194B', rgb: [230.0, 25.0, 25.0, 255.0] }, //Red
        { hex: '#3cb44b', rgb: [60.0, 180.0, 75.0, 255.0] }, //Green
        { hex: '#ffe119', rgb: [255.0, 225.0, 25.0, 255.0] }, //Yellow
        { hex: '#4363d8', rgb: [50.0, 80.0, 230.0, 255.0] }, //Blue
        { hex: '#f58231', rgb: [245.0, 130.0, 49.0, 255.0] }, //Orange
        { hex: '#911eb4', rgb: [145.0, 30.0, 180.0, 255.0] }, //Purple
        { hex: '#42d4f4', rgb: [66.0, 212.0, 244.0, 255.0] }, //Cyan
        { hex: '#bfef45', rgb: [191.0, 239.0, 69.0, 255.0] }, //Lime
        { hex: '#f032e6', rgb: [240.0, 50.0, 230.0, 255.0] }, //Magenta
        { hex: '#fabed4', rgb: [250.0, 190.0, 212.0, 255.0] }, //Pink
        { hex: '#469990', rgb: [70.0, 153.0, 144.0, 255.0] }, //Teal
        { hex: '#800000', rgb: [128.0, 0.0, 0.0, 255.0] }, //Maroon
        { hex: '#000075', rgb: [0.0, 0.0, 117.0, 255.0] }, //Navy
        { hex: '#9A6324', rgb: [154.0, 99.0, 36.0, 255.0] }, //Brown
        { hex: '#808000', rgb: [128.0, 128.0, 0.0, 255.0] }, //Olive
        { hex: '#aaffc3', rgb: [170.0, 255.0, 195.0, 255.0] }, //Mint
        { hex: '#dcbeff', rgb: [220.0, 190.0, 255.0, 255.0] }, //Lavender
        { hex: '#fffac8', rgb: [255.0, 250.0, 200.0, 255.0] }, //Beige
        { hex: '#ffd8b1', rgb: [255.0, 216.0, 177.0, 255.0] }, //Apricot
        { hex: '#a9a9a9', rgb: [169.0, 169.0, 169.0, 255.0] }, //Grey
        { hex: '#ffffff', rgb: [255.0, 255.0, 255.0, 255.0] }, //White
        { hex: '#000000', rgb: [0.0, 0.0, 0.0, 255.0] }, //Black
    ]

    //https://stackoverflow.com/questions/13806483/increase-or-decrease-color-saturation
    static saturate(d: number[], value: number) {
        let r = d[0]
        let g = d[1]
        let b = d[2]
        let a = d[3]
        let gray = 0.2989 * r + 0.587 * g + 0.114 * b //weights from CCIR 601 spec
        r = -gray * value + r * (1 + value)
        g = -gray * value + g * (1 + value)
        b = -gray * value + b * (1 + value)
        //normalize over- and under-saturated values
        if (r > 255) r = 255
        if (g > 255) g = 255
        if (b > 255) b = 255
        if (r < 0) r = 0
        if (g < 0) g = 0
        if (b < 0) b = 0
        return [r, g, b, a]
    }

    // Generate a random color in case there are more than the predefined ones needed.
    static generateColor() {
        let vm = this
        let hex = Math.floor(Math.random() * 16777215)
            .toString(16)
            .padStart(6, '0')
        let rgbParse = hex.match(/.{1,2}/g)
        if (null != rgbParse) {
            let rgb = [parseInt(rgbParse[0], 16), parseInt(rgbParse[1], 16), parseInt(rgbParse[2], 16), 255.0]
            vm.colorSet.push({
                hex: '#' + hex,
                rgb: rgb,
            })
        }
    }

    static createVectorFromAxis(signedAxis: string) {
        let sign = signedAxis[0] === '-' ? -1 : 1
        let axis = signedAxis[1]
        return [axis === 'X' ? sign : 0, axis === 'Y' ? sign : 0, axis === 'Z' ? sign : 0]
    }

    static entityTitle(entityType: string) {
        switch (entityType) {
            case EntityType.TRANSFORM:
                return 'Matrix Transform'
            case EntityType.PLANE:
                return 'Plane Coordinates'
            case EntityType.HOLE_PATTERN:
            case EntityType.ARC_PAIR:
            case EntityType.SLOT:
                return 'Hole Info'
            default:
                return 'Entity Info'
        }
    }

    static getNumberFormatter(places: number) {
        return function (num: number, roundPlaces: number = places) {
            let pow = Math.pow(10, roundPlaces)
            return Math.round(num * pow) / pow
        }
    }

    /**
     * Option Generators
     */

    static axes = ['', 'X', 'Y', 'Z']

    // Generate list of subcat options, excluding ones that are already selected
    static generateSubcatOptions(subcategoriesList: any[], selectedSubcategories: any[]) {
        return [''].concat(
            _.filter(subcategoriesList, function (subcat) {
                return !selectedSubcategories.includes(subcat)
            })
        )
    }

    // Generate list of axis options, excluding ones that are already selected
    static generateAxesOptions(datum: any, componentIdx: number) {
        let vm = this
        let usedAxes = _.map(datum.transformComponents.rotations, 'axis')
        usedAxes.length = componentIdx
        return _.filter(vm.axes, function (axis) {
            return !usedAxes.includes(axis)
        })
    }

    // Generate list of pre transform axis options, excluding ones that are already selected
    static generatePreTransformAxesOptions(selected: string, partInfo: any) {
        let vm = this
        let out: string[] = []
        _.forEach(vm.axes, function (axis) {
            // Include blank as an option if the value is currently unset
            if (axis === '' && (partInfo.preTransform.front === '' || partInfo.preTransform.up === '')) {
                out.push(axis)
            } else if (!selected.includes(axis)) {
                out.push('+' + axis)
                out.push('-' + axis)
            }
        })

        return out
    }

    /**
     * Conversion functions
     */

    static clearDatumTransform(datum: any) {
        datum.values = []
        datum.transformComponents.rotations = []
        datum.transformComponents.translateX = null
        datum.transformComponents.translateY = null
        datum.transformComponents.translateZ = null
    }

    static updateDatumTransformFromMatrix(datum: any, inMatrix: number[], formatNumber: Function) {
        let vm = this
        if (inMatrix != null && inMatrix.length > 0) {
            let matrix = _.map(inMatrix, (x) => formatNumber(x))
            let mat = math.matrix(matrix)
            datum.values = matrix
            let components = MatrixFunctions.decomposeRotation(mat)
            datum.transformComponents.rotations = _.map(components, function (component, index) {
                return {
                    axis: vm.axes[index + 1],
                    angle: MatrixFunctions.radToDeg(component),
                }
            })
            datum.transformComponents.translateX = mat.get([3])
            datum.transformComponents.translateY = mat.get([7])
            datum.transformComponents.translateZ = mat.get([11])
        }
    }

    static updateDatumTransformFromPlane(datum: any, matrix: number[], formatNumber: Function, computeDot?: boolean) {
        if (matrix != null && matrix.length > 0) {
            // Convert point/normal format into normal/distance format
            if (computeDot && matrix.length > 5) {
                let point = matrix.slice(0, 3)
                let norm = matrix.slice(3, 6)
                let dist = math.dot(point, norm)
                matrix = norm.concat([dist])
            }
            datum.values = _.map(matrix, (x) => formatNumber(x))
        }
    }

    static updateDatumTransformFromHolePattern(datum: any, data: any[]) {
        let vm = this
        // Clear values, updateDatumTransformFromSlot will append them
        datum.values = []
        _.forEach(data, function (entry) {
            return vm.updateDatumTransformFromHole(datum, entry, true)
        })
    }

    static updateDatumTransformFromHole(datum: any, data: any, append?: boolean) {
        // x,   y,   z,   // slot or hole center
        // dx1, dy1, dz1, // basis vec. 1, oriented in shorter axis direction (length gives radius)
        // dx2, dy2, dz2, // basis vec. 2 perpendicular to basis vector 1, has same length as basis vector 1 if hole
        // dx3, dy3, dz3, // basis vec. 3, perp to basis vectors 1 and 2, length gives half depth
        let center1 = [data.center1.x, data.center1.y, data.center1.z]
        let center2 = [data.center2.x, data.center2.y, data.center2.z]

        let center0: number[] = [
            (center2[0] + center1[0]) / 2,
            (center2[1] + center1[1]) / 2,
            (center2[2] + center1[2]) / 2,
        ]
        let d1: number[]
        let d2: number[]
        let d3: number[] = [center1[0] - center0[0], center1[1] - center0[1], center1[2] - center0[2]]

        // Intermediate vector for doing cross products
        let vec: number[] = []
        switch (data.type) {
            case HolePatternType.HOLE:
                // Find axis with min dot product
                let axes = [
                    [1, 0, 0],
                    [0, 1, 0],
                    [0, 0, 1],
                ]
                vec = _.reduce(
                    axes,
                    function (axis, best) {
                        if (Math.abs(math.dot(d3, best)) > Math.abs(math.dot(d3, axis))) {
                            return axis
                        }
                        return best
                    },
                    d3
                )
                break
            case HolePatternType.SLOT:
                vec = [data.slotDir.x, data.slotDir.y, data.slotDir.z]
                break
        }

        // Cross product of d3 and vec, length is radius
        d1 = math.multiply(MatrixFunctions.normalize(math.cross(d3, vec) as number[]), data.radius)
        // Cross product of d3 and d1, length is radius + 1/2 width
        d2 = math.multiply(MatrixFunctions.normalize(math.cross(d3, d1) as number[]), data.width / 2 + data.radius)

        // Final format:
        // T = [[ dx1, dx2, dx3, x ],
        //      [ dy1, dy2, dy3, y ],
        //      [ dz1, dz2, dz3, z ],
        //      [ 0,   0,   0,   1 ]]

        d1.push(0)
        d2.push(0)
        d3.push(0)
        center0.push(1)
        let tmp = math.transpose(math.matrix([d1, d2, d3, center0]))

        if (!append) {
            datum.values = []
        }

        datum.values.push(...math.flatten(tmp).toArray())
    }

    static updateDatumTransformFromComponents(datum: any, componentIdx: number, formatNumber: Function) {
        // Process component updates
        if (componentIdx >= 0) {
            // Check if it's a rotation axis change
            // Truncate list
            datum.transformComponents.rotations.length = componentIdx + 1
            if (componentIdx < 2) {
                datum.transformComponents.rotations.push({
                    axis: '',
                    angle: 0.0,
                })
            }
        }

        let angles: number[] = _.map(datum.transformComponents.rotations, 'angle')
        let mat = MatrixFunctions.composeRotation(
            _.map(angles, MatrixFunctions.degToRad),
            _.map(datum.transformComponents.rotations, 'axis')
        )

        mat.set([3], datum.transformComponents.translateX)
        mat.set([7], datum.transformComponents.translateY)
        mat.set([11], datum.transformComponents.translateZ)

        datum.values = mat.toArray()
        datum.values = _.map(datum.valuies, (x) => formatNumber(x))
    }
}
